// Audit (admin only) (admin only). Two parts:
//   - Who handled it (QL-20): per week, how many contacts the AI handled
//     alone, a person sent as the AI drafted, a person changed, or a person
//     handled alone. CXHub's own data. A count of contacts, never of people.
//   - The QA auditor: what the independent observer (qa-observer/) has
//     recorded and its verdicts (phase 2: the contact check), read live from
//     its own API through CXHub's read-only proxy. CXHub stores none of it.
//     The fails list is for reading a sample: rule, outcome, reason, and a
//     link to the contact.
//   - The day-7 audit (phase 3, QaAuditPanel.jsx): the agreement with human
//     rulings, the weekly sample to rule on, and the audit records.
//   - Agent agreement (2026-10-23, ShadowAgreementPanel.jsx): per intent, how
//     the agent's drafts compared with what was sent and processed. No
//     per-agent numbers; the ready hint switches nothing.
//   - Improvement (phase 4, QualityLoopPanel.jsx): the AI share next to the
//     auditor, the gaps, candidate fixes replayed with nothing sent and
//     graded blind by the auditor, and the report to Jenn. Its own switches;
//     it applies nothing.
// The one thing on this page that writes is a human ruling, into the
// auditor's own database (QaAuditPanel.jsx); nothing changes CXHub's data.
// Patrick owns the reporting views; this is a plain table until he designs
// them.

import { useEffect, useState } from 'react'
import {
  Alert, Box, CircularProgress, Link, MenuItem, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material'
import { fetchHandlingTrend, fetchQaObserver } from './api.js'
import { convNumberPath } from './contactPath.js'
import QaAuditPanel from './QaAuditPanel.jsx'
import QualityLoopPanel from './QualityLoopPanel.jsx'
import ShadowAgreementPanel from './ShadowAgreementPanel.jsx'

const CLASS_LABEL = {
  ai_alone: 'AI alone',
  ai_unchanged: 'AI, sent unchanged',
  ai_changed: 'AI, changed by a person',
  person_alone: 'Person alone',
}

function pct(v) {
  return v == null ? '—' : `${Math.round(v * 100)}%`
}

function when(v) {
  return v ? new Date(v).toLocaleString() : '—'
}

function HandlingTrend() {
  const [data, setData] = useState(null)
  const [error, setError] = useState(null)
  useEffect(() => {
    fetchHandlingTrend().then(setData).catch((e) => setError(e.message))
  }, [])
  if (error) return <Alert severity="error">Could not load the trend: {error}</Alert>
  if (!data) return <CircularProgress size={24} />
  return (
    <Stack spacing={1}>
      <Typography variant="body2" color="text.secondary">
        {data.from} to {data.to}. The AI share counts AI alone and AI sent unchanged.
        A signature added or a placeholder filled is not a change.
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Week ending</TableCell>
            {data.classes.map((c) => <TableCell key={c} align="right">{CLASS_LABEL[c] ?? c}</TableCell>)}
            <TableCell align="right">Total</TableCell>
            <TableCell align="right">AI share</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.weeks.map((w) => (
            <TableRow key={w.weekEnding}>
              <TableCell>{w.weekEnding}</TableCell>
              {data.classes.map((c) => <TableCell key={c} align="right">{w.counts[c]}</TableCell>)}
              <TableCell align="right">{w.total}</TableCell>
              <TableCell align="right">{pct(w.aiShare)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {Object.keys(data.reasons ?? {}).length > 0 && (
        <Typography variant="body2" color="text.secondary">
          Why a contact counted as changed: {Object.entries(data.reasons).map(([r, n]) => `${r.replaceAll('_', ' ')} ${n}`).join(', ')}
        </Typography>
      )}
    </Stack>
  )
}

function Fails() {
  const [rules, setRules] = useState([])
  const [ruleId, setRuleId] = useState('')
  const [fails, setFails] = useState(null)
  const [error, setError] = useState(null)
  useEffect(() => {
    fetchQaObserver('rules').then((r) => setRules(r.rules ?? [])).catch(() => setRules([]))
  }, [])
  useEffect(() => {
    setFails(null)
    fetchQaObserver('fails', { ruleId: ruleId || null, limit: 50 })
      .then((r) => { setFails(r.fails ?? []); setError(null) })
      .catch((e) => setError(e.message))
  }, [ruleId])
  const nameOf = Object.fromEntries(rules.map((r) => [r.id, r.name]))
  return (
    <Stack spacing={1}>
      <Typography variant="subtitle1">Fails, newest first</Typography>
      <Typography variant="body2" color="text.secondary">
        Read-only: a fail blocks nothing and sends nothing. Rule texts, critical flags and the judgment thresholds are
        placeholders until CS management sets them.
      </Typography>
      <TextField select size="small" label="Rule" value={ruleId} onChange={(e) => setRuleId(e.target.value)} sx={{ maxWidth: 420 }}>
        <MenuItem value="">All rules</MenuItem>
        {rules.map((r) => <MenuItem key={r.id} value={r.id}>{r.id}</MenuItem>)}
      </TextField>
      {error && <Alert severity="error">Could not load the fails: {error}</Alert>}
      {!error && !fails && <CircularProgress size={24} />}
      {fails && fails.length === 0 && <Typography variant="body2">No fails.</Typography>}
      {fails && fails.length > 0 && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Contact</TableCell><TableCell>Rule</TableCell><TableCell>Outcome</TableCell><TableCell>Reason</TableCell>
              <TableCell>By</TableCell><TableCell>Channel</TableCell><TableCell>Checked</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {fails.map((f) => (
              <TableRow key={`${f.conv_number}-${f.rule_id}`}>
                <TableCell><Link href={convNumberPath(f.conv_number)}>{f.conv_number}</Link></TableCell>
                <TableCell title={nameOf[f.rule_id] ?? ''}>{f.rule_id}{f.critical ? ' (critical)' : ''}</TableCell>
                <TableCell>{f.outcome}</TableCell>
                <TableCell>{f.reason ?? '—'}{f.subject ? ` [${f.subject}]` : ''}</TableCell>
                <TableCell>{f.source === 'model' ? `model ${f.model ?? ''}` : 'code'}{f.automated ? ', automated' : ''}</TableCell>
                <TableCell>{f.channel}</TableCell>
                <TableCell>{when(f.created_at)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Stack>
  )
}

function Auditor() {
  const [status, setStatus] = useState(null)
  const [records, setRecords] = useState([])
  const [measurements, setMeasurements] = useState([])
  const [error, setError] = useState(null)
  useEffect(() => {
    Promise.all([fetchQaObserver('status'), fetchQaObserver('records', { limit: 50 }), fetchQaObserver('measurements')])
      .then(([s, r, m]) => { setStatus(s); setRecords(r.records ?? []); setMeasurements(m.measurements ?? []) })
      .catch((e) => setError(e.message))
  }, [])
  if (error) {
    return error.includes('503')
      ? <Alert severity="info">The QA auditor is not connected in this environment yet (QA_OBSERVER_VIEW_ENABLED).</Alert>
      : <Alert severity="error">Could not reach the QA auditor: {error}</Alert>
  }
  if (!status) return <CircularProgress size={24} />
  return (
    <Stack spacing={2}>
      <Typography variant="body2">
        {status.records} contacts recorded, {status.recordsLastDay} in the last day. Pulled up to {when(status.watermark)}.
        Last run: {status.lastRun ? `${status.lastRun.status}, ${status.lastRun.items} contacts, ${when(status.lastRun.finished_at ?? status.lastRun.started_at)}` : 'none yet'}.
      </Typography>
      <Typography variant="body2">
        Models: active {status.models?.active}; audit calls {status.models?.callsEnabled ? 'on' : 'off'};
        spend this month ${Number(status.modelSpendThisMonthUsd ?? 0).toFixed(2)} of ${status.models?.monthlyLimitUsd ?? 0}.
      </Typography>
      {status.models?.problems?.length > 0 && <Alert severity="warning">{status.models.problems.join('; ')}</Alert>}
      {status.check && (
        <Typography variant="body2">
          Contact check {status.check.enabled ? 'on' : 'off'} (scope {status.check.scope}, rules {status.check.catalogVersion}).
          Last 7 days: {status.check.automatedLast7Days?.checked ?? 0} of {status.check.automatedLast7Days?.contacts ?? 0} automated
          contacts checked; {status.check.otherLast7Days?.checked ?? 0} of {status.check.otherLast7Days?.contacts ?? 0} others.
        </Typography>
      )}
      <Fails />
      <QaAuditPanel status={status} />

      {measurements.length > 0 && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Measured</TableCell><TableCell>Model</TableCell><TableCell align="right">Contacts</TableCell>
              <TableCell align="right">Tokens in / out</TableCell><TableCell align="right">Per contact</TableCell><TableCell align="right">Per month</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {measurements.map((m) => (
              <TableRow key={m.id}>
                <TableCell>{when(m.at)}</TableCell><TableCell>{m.deployment}</TableCell><TableCell align="right">{m.sample_size}</TableCell>
                <TableCell align="right">{Math.round(m.avg_prompt_tokens)} / {Math.round(m.avg_completion_tokens)}</TableCell>
                <TableCell align="right">${Number(m.avg_cost_per_contact_usd).toFixed(5)}</TableCell>
                <TableCell align="right">${Number(m.monthly_cost_estimate_usd).toFixed(2)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Contact</TableCell><TableCell>Channel</TableCell><TableCell>Account</TableCell><TableCell>Intent</TableCell>
            <TableCell>Closed by</TableCell><TableCell>Handled</TableCell><TableCell>Order (auditor's read)</TableCell><TableCell>Activity</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {records.map((r) => (
            <TableRow key={r.conv_number}>
              <TableCell>{r.conv_number}</TableCell><TableCell>{r.channel}</TableCell><TableCell>{r.account ?? '—'}</TableCell>
              <TableCell>{r.intent ?? '—'}</TableCell><TableCell>{r.close_path ?? '—'}</TableCell>
              <TableCell>{CLASS_LABEL[r.handling_class] ?? '—'}</TableCell>
              <TableCell>{r.order_facts ? (r.order_facts.found ? (r.order_facts.status ?? 'found') : (r.order_facts.error ? 'read failed' : 'not found')) : '—'}</TableCell>
              <TableCell>{when(r.activity_at)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Stack>
  )
}

export default function QaObserverView() {
  return (
    <Box sx={{ p: 2 }}>
      <Stack spacing={3}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>Who handled it</Typography>
          <HandlingTrend />
        </Paper>
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>QA auditor</Typography>
          <Auditor />
        </Paper>
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>Agent agreement</Typography>
          <ShadowAgreementPanel />
        </Paper>
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>Improvement</Typography>
          <QualityLoopPanel />
        </Paper>
      </Stack>
    </Box>
  )
}
